import { useQuery } from "@tanstack/react-query"
import { SearchIcon } from "lucide-react"
import type { MouseEvent, RefObject } from "react"
import { useLocation } from "react-router"
import { inboxUnreadQuery } from "@/beacon-inbox/client.ts"
import { unreadsQuery } from "@/chat/client.ts"
import {
  Disc,
  TabBar,
  TabBarTab,
  useFullScreenTaskOpen,
  useKeyboardUp,
  type PageScrollHandle,
} from "@/design-system/index.ts"
import { useUpdate } from "@/installed-app/updates.ts"
import { useSidebar } from "@/primitives/sidebar.tsx"
import type { Me } from "@/sign-in/client.ts"
import { currentTabOf, destinations } from "./destinations.ts"

/*
 * The shell's tab bar below 1024px, where Beacon is laid out for a phone: along the bottom, under
 * the page, it holds Home, My tickets, Chat and Projects, then Search (the command palette, full screen)
 * and the Member's avatar, which opens the sidebar as a drawer of everything else (`AppSidebar`
 * renders it on the compact layout), and wears a dot while a new release of Beacon waits there.
 * Chat wears a dot ("Unread") while anything is unread to the Member there.
 * `Shell` renders the tab bar after the page panel, so a screen reader and Tab reach the page
 * first.
 */

/** The pages with a tab of their own, in the order the tab bar shows them. */
const tabbed = destinations.filter((destination) => destination.tab)

/**
 * The tab bar, which steps aside while the keyboard is up or a full-screen task is open. `page` is
 * the panel's scroll container, which the current tab returns to its top.
 */
export function ShellTabBar({
  me,
  openTickets,
  onSearch,
  page,
}: {
  me: Me
  openTickets: number | undefined
  onSearch: () => void
  page: RefObject<PageScrollHandle | null>
}) {
  const { pathname } = useLocation()
  const { setOpenMobile } = useSidebar()
  const current = currentTabOf(pathname)
  const { state } = useUpdate()
  // The tab bar steps aside for the keyboard and for a task that takes the whole screen.
  const keyboardUp = useKeyboardUp()
  const fullScreen = useFullScreenTaskOpen()
  const hidden = keyboardUp || fullScreen
  // Chat wears a dot while a joined conversation or a followed thread holds something new, or a
  // mention or a notice in the Beacon inbox is unread.
  const unreads = useQuery(unreadsQuery).data
  const inbox = useQuery(inboxUnreadQuery).data?.unread ?? 0
  const chatUnread =
    inbox > 0 || Boolean(unreads && unreads.conversations + unreads.threads + unreads.mentions > 0)

  // The tab for the page already showing returns it to its top rather than opening it again; on a
  // page beneath it (a Board, under Projects), it goes back up to the tab's own page.
  const onTab = (to: string) => (event: MouseEvent<HTMLElement>) => {
    if (pathname !== to) return
    event.preventDefault()
    page.current?.scrollToTop()
  }

  return (
    <TabBar label="Tabs" className={hidden ? "hidden" : undefined}>
      {tabbed.map(({ to, title, tabTitle, Icon }) => (
        <TabBarTab
          key={to}
          to={to}
          label={tabTitle ?? title}
          icon={<Icon aria-hidden />}
          current={current === to}
          count={to === "/my-tickets" ? openTickets : undefined}
          {...(to === "/chat" && chatUnread && { dot: "Unread" })}
          onSelect={onTab(to)}
        />
      ))}
      <TabBarTab label="Search" icon={<SearchIcon aria-hidden />} onSelect={onSearch} />
      <TabBarTab
        label="You"
        icon={
          <Disc
            person={me}
            size={24}
            ring={
              current === "you"
                ? "ring-1 ring-sidebar-foreground ring-offset-1 ring-offset-sidebar"
                : undefined
            }
          />
        }
        current={current === "you"}
        dot={state.status === "current" ? undefined : "Update ready"}
        onSelect={() => setOpenMobile(true)}
      />
    </TabBar>
  )
}
